import { atom } from "jotai";

/**
 * Set to `true` once the `<PageContextProvider>` (in
 * `@granular/fabric3-library-page-context-bar`) has finished its hydration
 * pass — i.e. it has stopped *trying* to load filter state. `useFilterState`'s
 * `status.loading` (and `apps/map`'s `useMainPage`) flip to `false` based on
 * this.
 *
 * The provider is now the single, unconditional source of truth for
 * page-context hydration, so this is the only signal consumers need to track
 * hydration progress.
 *
 * The atom lives in core (rather than the page-context-bar library) because
 * core consumers need to read it and core does not depend on page-context-bar.
 * The page-context-bar library re-exports it for convenience.
 *
 * **Contract:** `true` means "done", not "succeeded". The provider also flips
 * this to `true` when its IndexedDB read throws, so consumers MUST NOT treat
 * `status.loading === false` as a guarantee that filters loaded successfully.
 * If we need to distinguish success from failure in the future, add a separate
 * error atom rather than overloading this one.
 */
export const pageContextProviderHydratedAtom = atom<boolean>(false);

/**
 * How many `<PageContextProvider>`s that own their page's filter state are
 * mounted — every provider except those with `deferToPageProvider`.
 *
 * More than one provider can be mounted at once: the Container renders the
 * CARL chatbot (which mounts a deferring provider) alongside every app's own
 * provider. A deferring provider reads this to leave filter state to the page.
 * Counting, rather than a boolean, keeps one provider's unmount from marking
 * the others as gone.
 */
export const pageProviderCountAtom = atom<number>(0);
